import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Gift, Copy, Users, CreditCard, ArrowLeft, Check, Share2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Navbar from "../components/Navbar";

const ReferralPage = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [referralData, setReferralData] = useState(null);
  const [referralCode, setReferralCode] = useState("");
  const [applyCode, setApplyCode] = useState("");
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      const [codeRes, statsRes] = await Promise.all([
        axios.get(`${API}/referral/my-code`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setReferralData({ ...codeRes.data, ...statsRes.data });
      setReferralCode(codeRes.data.referral_code);
    } catch (error) {
      console.error("Failed to fetch referral data");
    } finally {
      setLoading(false);
    }
  };

  const copyCode = () => {
    const shareUrl = `${window.location.origin}/auth?ref=${referralCode}`;
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success("Referral link copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleApplyCode = async () => {
    if (!applyCode.trim()) return;
    setApplying(true);
    try {
      const response = await axios.post(
        `${API}/referral/apply`,
        { referral_code: applyCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchReferralData();
      setApplyCode("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to apply referral code");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0]">
      <Navbar />

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#5C5D5C] hover:text-[#1A1B1A] mb-8">
          <ArrowLeft size={20} /> Back
        </button>

        <div className="text-center mb-12">
          <div className="w-16 h-16 rounded-full bg-[#F06570]/10 flex items-center justify-center mx-auto mb-4">
            <Gift className="text-[#F06570]" size={32} />
          </div>
          <h1 className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light text-[#1A1B1A]">
            Referral Programme
          </h1>
          <p className="text-[#5C5D5C] mt-4 max-w-xl mx-auto font-light">
            Share ARKY World with friends and earn 10% of their first purchase as credit.
          </p>
        </div>

        {/* Your Referral Code */}
        <div className="dashboard-card mb-8" data-testid="referral-code-section">
          <h2 className="font-medium text-[#1A1B1A] mb-4">Your Referral Link</h2>
          <div className="flex gap-3">
            <Input
              value={`${window.location.origin}/auth?ref=${referralCode}`}
              readOnly
              className="flex-1 rounded-none border-[#EAE6DF] bg-[#F7F5F0]"
              data-testid="referral-link"
            />
            <Button
              onClick={copyCode}
              className="bg-[#20C997] hover:bg-[#1AAE82] text-white rounded-none px-6"
              data-testid="copy-referral-btn"
            >
              {copied ? <Check size={18} /> : <Copy size={18} />}
              <span className="ml-2">{copied ? "Copied" : "Copy"}</span>
            </Button>
          </div>
          <p className="text-sm text-[#5C5D5C] mt-3">
            Your code: <span className="font-mono font-medium text-[#20C997]">{referralCode}</span>
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="dashboard-card text-center">
            <Users className="mx-auto text-[#20C997] mb-2" size={28} />
            <p className="text-3xl font-light text-[#1A1B1A]" data-testid="referral-count">
              {referralData?.total_referrals_count || 0}
            </p>
            <p className="text-sm text-[#5C5D5C] mt-1">People Referred</p>
          </div>
          <div className="dashboard-card text-center">
            <CreditCard className="mx-auto text-[#F06570] mb-2" size={28} />
            <p className="text-3xl font-light text-[#1A1B1A]" data-testid="total-earned">
              £{referralData?.total_earned?.toFixed(2) || "0.00"}
            </p>
            <p className="text-sm text-[#5C5D5C] mt-1">Total Earned</p>
          </div>
          <div className="dashboard-card text-center">
            <Gift className="mx-auto text-[#3E6656] mb-2" size={28} />
            <p className="text-3xl font-light text-[#1A1B1A]" data-testid="credit-balance">
              £{referralData?.credit_balance?.toFixed(2) || "0.00"}
            </p>
            <p className="text-sm text-[#5C5D5C] mt-1">Credit Balance</p>
          </div>
        </div>

        {/* Apply Code */}
        {!referralData?.referred_by && (
          <div className="dashboard-card mb-8" data-testid="apply-referral-section">
            <h2 className="font-medium text-[#1A1B1A] mb-4">Have a Referral Code?</h2>
            <div className="flex gap-3">
              <Input
                value={applyCode}
                onChange={(e) => setApplyCode(e.target.value)}
                placeholder="Enter referral code (e.g. ARKY-XXXXXX)"
                className="flex-1 rounded-none border-[#EAE6DF]"
                data-testid="apply-code-input"
              />
              <Button
                onClick={handleApplyCode}
                disabled={applying || !applyCode.trim()}
                className="bg-[#F06570] hover:bg-[#D9525D] text-white rounded-none px-6"
                data-testid="apply-code-btn"
              >
                {applying ? "Applying..." : "Apply"}
              </Button>
            </div>
          </div>
        )}

        {referralData?.referred_by && (
          <div className="bg-[#3E6656]/10 border border-[#3E6656] p-4 mb-8 text-center">
            <Check className="mx-auto text-[#3E6656] mb-2" size={24} />
            <p className="text-[#3E6656] font-medium">You were referred by {referralData.referred_by.referrer_name}</p>
          </div>
        )}

        {/* Referral History */}
        {referralData?.referrals_made?.length > 0 && (
          <div className="dashboard-card" data-testid="referral-history">
            <h2 className="font-medium text-[#1A1B1A] mb-4">Your Referrals</h2>
            <div className="space-y-3">
              {referralData.referrals_made.map((ref, index) => (
                <div key={ref.id} className="flex items-center justify-between p-3 bg-[#F7F5F0]" data-testid={`referral-${index}`}>
                  <div>
                    <p className="font-medium text-[#1A1B1A]">{ref.referred_name}</p>
                    <p className="text-xs text-[#5C5D5C]">
                      Joined {new Date(ref.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className={`text-sm font-medium ${ref.first_purchase_made ? "text-[#3E6656]" : "text-[#D4A373]"}`}>
                      {ref.first_purchase_made ? `£${ref.credit_earned?.toFixed(2)} earned` : "Awaiting first purchase"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* How It Works */}
        <div className="mt-12">
          <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1B1A] text-center mb-8">
            How It Works
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <div className="w-10 h-10 rounded-full bg-[#20C997] text-white flex items-center justify-center mx-auto mb-4 font-medium">1</div>
              <h3 className="font-medium text-[#1A1B1A] mb-2">Share Your Link</h3>
              <p className="text-sm text-[#5C5D5C]">Copy your unique referral link and share it with friends and colleagues</p>
            </div>
            <div className="text-center p-6">
              <div className="w-10 h-10 rounded-full bg-[#20C997] text-white flex items-center justify-center mx-auto mb-4 font-medium">2</div>
              <h3 className="font-medium text-[#1A1B1A] mb-2">They Sign Up & Shop</h3>
              <p className="text-sm text-[#5C5D5C]">Your friend creates an account and makes their first purchase</p>
            </div>
            <div className="text-center p-6">
              <div className="w-10 h-10 rounded-full bg-[#20C997] text-white flex items-center justify-center mx-auto mb-4 font-medium">3</div>
              <h3 className="font-medium text-[#1A1B1A] mb-2">You Earn Credit</h3>
              <p className="text-sm text-[#5C5D5C]">Receive 10% of their first purchase as store credit</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReferralPage;
